import { useState } from "react";
import axios from "axios";
import { Plus, Trash2, Settings2, Loader2, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import { API, timeAgo } from "@/lib/newsUtils";

export const SourcesPanel = ({ sources, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [domain, setDomain] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!domain.trim()) {
      toast.error("Introduce el dominio del medio.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/me/sources`, {
        domain: domain.trim(),
        name: name.trim() || undefined,
      });
      toast.success(`Fuente añadida: ${res.data.name}. Buscando noticias...`);
      setDomain("");
      setName("");
      onChanged();
    } catch (err) {
      const msg = err.response?.data?.detail || "No se pudo añadir la fuente.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, srcName) => {
    try {
      await axios.delete(`${API}/me/sources/${id}`);
      toast.success(`Fuente eliminada: ${srcName}`);
      onChanged();
    } catch {
      toast.error("No se pudo eliminar la fuente.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="manage-sources-button"
          variant="outline"
          className="rounded-none border-border h-10 w-10 px-0 sm:w-auto sm:px-4 gap-2"
        >
          <Settings2 className="w-4 h-4" />
          <span className="hidden sm:inline">Fuentes</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="sources-panel">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-black tracking-tight">
            Gestionar fuentes
          </DialogTitle>
          <DialogDescription>
            Añade un medio escribiendo solo su dominio. Detectamos su feed RSS automáticamente.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleAdd} className="space-y-4 border border-border p-5 bg-muted/30">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="domain" className="text-xs uppercase tracking-wide font-semibold">
                Dominio del medio *
              </Label>
              <Input
                id="domain"
                data-testid="source-domain-input"
                placeholder="ej. ejemplo.com"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                className="rounded-none border-border"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-xs uppercase tracking-wide font-semibold">
                Nombre (opcional)
              </Label>
              <Input
                id="name"
                data-testid="source-name-input"
                placeholder="Se detecta automáticamente"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="rounded-none border-border"
              />
            </div>
          </div>
          <Button
            type="submit"
            data-testid="submit-source-button"
            disabled={submitting}
            className="rounded-none bg-primary hover:bg-blue-900 w-full h-11 gap-2"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {submitting ? "Buscando feed..." : "Añadir fuente"}
          </Button>
        </form>

        <div className="space-y-2">
          <p className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">
            {sources.length} fuentes activas
          </p>
          <div className="divide-y divide-border border border-border overflow-hidden">
            {sources.map((s) => {
              const isGnews = (s.feed_url || "").includes("news.google.com");
              return (
              <div
                key={s.id}
                data-testid={`source-row-${s.id}`}
                className="flex items-center justify-between gap-3 p-3 min-w-0"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <p className="font-semibold text-sm truncate">{s.name}</p>
                    {isGnews && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            data-testid={`source-info-${s.id}`}
                            className="shrink-0 text-primary/70 hover:text-primary transition-colors"
                            aria-label="Por qué usamos Google News"
                          >
                            <Info className="w-3.5 h-3.5" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent
                          side="top"
                          align="start"
                          className="rounded-none w-72 text-sm"
                          data-testid={`source-info-content-${s.id}`}
                        >
                          <p className="font-semibold mb-1 flex items-center gap-1.5">
                            <Info className="w-4 h-4 text-primary" /> Vía Google News
                          </p>
                          <p className="text-muted-foreground leading-relaxed">
                            Este medio no ofrece un feed RSS público, así que obtenemos sus
                            noticias a través de Google News. El resultado es el mismo: te
                            enlazamos siempre al artículo original del medio.
                          </p>
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    {isGnews ? "Google News" : s.feed_url}
                  </p>
                  <p
                    data-testid={`source-updated-${s.id}`}
                    className="text-[11px] text-muted-foreground/70 truncate mt-0.5"
                  >
                    {timeAgo(s.last_fetched_at)}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    data-testid={`delete-source-${s.id}`}
                    onClick={() => handleDelete(s.id, s.name)}
                    className="p-2 text-muted-foreground hover:text-destructive transition-colors"
                    aria-label={`Eliminar ${s.name}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
